import { useNavigate } from "react-router-dom";
import { Building2, Check, LogOut, Settings, User as UserIcon } from "lucide-react";
import { useTheme } from "next-themes";
import { Moon, Sun, Monitor } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/features/auth/store";
import { useLogout, useSwitchWorkspace, useWorkspaces } from "@/features/auth/hooks";

function initialsOf(first?: string, last?: string, email?: string) {
  if (first || last) {
    return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
  }
  return (email?.[0] ?? "?").toUpperCase();
}

export default function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { data: workspaceData } = useWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const workspaces =
    workspaceData?.enabled && workspaceData.workspaces.length > 1
      ? workspaceData.workspaces
      : null;

  async function handleLogout() {
    await logout.mutateAsync();
    navigate("/login", { replace: true });
  }

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 gap-2 rounded-full pl-1 pr-3"
          aria-label="Account menu"
        >
          <Avatar className="size-7">
            {user.avatar_url && <AvatarImage src={user.avatar_url} alt="" />}
            <AvatarFallback>
              {initialsOf(user.first_name, user.last_name, user.email)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden text-sm sm:inline">
            {user.first_name || user.email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {user.email}
            </span>
          </div>
        </DropdownMenuLabel>
        {workspaces && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs">Workspace</DropdownMenuLabel>
            {workspaces.map((w) => (
              <DropdownMenuItem
                key={w.tenant.id}
                disabled={w.is_current || switchWorkspace.isPending}
                onClick={() => switchWorkspace.mutate(w.tenant.id)}
              >
                <Building2 className="size-4" />
                <span className="flex-1 truncate">{w.tenant.name}</span>
                {w.is_current && <Check className="size-4" />}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {/* Everyone gets Settings, customers included — it is where they
            change their own password. The tabs inside it are what differ. */}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")}>
          <UserIcon className="size-4" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings")}>
          <Settings className="size-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs">Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme ?? "system"}
          onValueChange={setTheme}
        >
          <DropdownMenuRadioItem value="light">
            <Sun className="mr-2 size-4" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="mr-2 size-4" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="mr-2 size-4" /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
